import { CartAddEvent } from '@theme/events';

/**
 * Cart Order Bump Product Component
 * Manages individual product interactions in the order bump carousel
 */
class CartOrderBumpProduct extends HTMLElement {
  constructor() {
    super();
    this.productData = null;
    this.variantsData = null;
    this.currentVariantId = null;
  }

  connectedCallback() {
    this.#loadProductData();
    this.#setupEventListeners();
    this.currentVariantId = this.getAttribute('data-default-variant-id');
  }

  /**
   * Loads product and variants data from script tags
   */
  #loadProductData() {
    try {
      const productScript = this.querySelector('script[data-product]');
      const variantsScript = this.querySelector('script[data-variants]');
      
      if (productScript && variantsScript && productScript.textContent && variantsScript.textContent) {
        this.productData = JSON.parse(productScript.textContent);
        this.variantsData = JSON.parse(variantsScript.textContent);
      }
    } catch (error) {
      console.error('Error loading product data:', error);
    }
  }

  /**
   * Sets up event listeners for variant selection and add to cart
   */
  #setupEventListeners() {
    // Listen for variant changes
    this.addEventListener('change', (event) => {
      if (event.target instanceof HTMLSelectElement && event.target.classList.contains('cart-order-bump__variant-select')) {
        this.#handleVariantChange();
      }
    });

    // Listen for add to cart button clicks
    this.addEventListener('click', (event) => {
      if (event.target instanceof HTMLElement && event.target.closest('[data-add-button]')) {
        event.preventDefault();
        this.#handleAddToCart();
      }
    });
  }

  /**
   * Handles variant selection changes
   */
  #handleVariantChange() {
    const selects = this.querySelectorAll('.cart-order-bump__variant-select');
    const selectedOptions = Array.from(selects).map(select => {
      return select instanceof HTMLSelectElement ? select.value : '';
    });
    
    const selectedVariant = this.#findVariantByOptions(selectedOptions);
    
    if (selectedVariant) {
      this.currentVariantId = String(selectedVariant.id);
      this.#updateProductDisplay(selectedVariant);
    }
  }

  /**
   * Finds variant by selected options
   * @param {string[]} selectedOptions 
   * @returns {any|null}
   */
  #findVariantByOptions(selectedOptions) {
    if (!this.variantsData) return null;
    return this.variantsData.find((/** @type {any} */ variant) => {
      return variant.options && variant.options.every((/** @type {any} */ option, /** @type {number} */ index) => option === selectedOptions[index]);
    }) || null;
  }

  /**
   * Updates product display based on selected variant
   * @param {any} variant 
   */
  #updateProductDisplay(variant) {
    // Update image
    const img = this.querySelector('[data-product-image]');
    if (img instanceof HTMLImageElement && variant?.featured_image) {
      img.src = variant.featured_image.src.replace(/\.(jpg|jpeg|png|gif|webp)/, '_200x200.$1');
      img.alt = variant.featured_image.alt || this.productData?.title || '';
    }

    // Update prices
    this.#updatePrices(variant);

    // Update availability
    this.#updateAvailability(variant);

    // Update button variant ID
    const button = this.querySelector('[data-add-button]');
    if (button && variant?.id) {
      button.setAttribute('data-variant-id', String(variant.id));
    }
  }

  /**
   * Updates price display
   * @param {any} variant 
   */
  #updatePrices(variant) {
    const priceContainer = this.querySelector('[data-price-container]');
    const comparePrice = this.querySelector('[data-compare-price]');
    const discountLabel = this.querySelector('[data-discount-label]');
    const finalPrice = this.querySelector('[data-final-price]');

    if (!finalPrice) return;

    // Update final price
    finalPrice.textContent = this.#formatMoney(variant?.price || 0);

    // Handle compare price and discount
    if (variant?.compare_at_price && variant.compare_at_price > variant.price) {
      if (comparePrice instanceof HTMLElement) {
        comparePrice.textContent = this.#formatMoney(variant.compare_at_price);
        comparePrice.style.display = '';
      }

      if (discountLabel instanceof HTMLElement) {
        const discountPercent = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
        discountLabel.textContent = `${discountPercent}% OFF`;
        discountLabel.style.display = '';
      }
    } else {
      if (comparePrice instanceof HTMLElement) comparePrice.style.display = 'none';
      if (discountLabel instanceof HTMLElement) discountLabel.style.display = 'none';
    }
  }

  /**
   * Updates availability state
   * @param {any} variant 
   */
  #updateAvailability(variant) {
    const button = this.querySelector('[data-add-button]');
    const buttonText = this.querySelector('[data-add-text]');

    if (!(button instanceof HTMLButtonElement) || !buttonText) return;

    if (variant?.available) {
      button.disabled = false;
      buttonText.textContent = 'Quero aproveitar';
    } else {
      button.disabled = true;
      buttonText.textContent = 'Indisponível';
    }
  }

  /**
   * Handles add to cart button click
   */
  async #handleAddToCart() {
    const button = this.querySelector('[data-add-button]');
    const spinner = this.querySelector('[data-spinner]');
    const buttonText = this.querySelector('[data-add-text]');

    if (!this.currentVariantId || !(button instanceof HTMLButtonElement) || button.disabled) return;

    // Show loading state
    this.#setLoadingState(true);

    try {
      const formData = {
        id: this.currentVariantId,
        quantity: 1
      };

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const result = await response.json();
        
        // Show success state briefly
        this.#setSuccessState();
        
        // Dispatch custom order bump event for analytics
        this.#dispatchOrderBumpEvent(result);
        
        // Dispatch CartAddEvent to update cart drawer (this handles everything automatically)
        this.#dispatchCartUpdateEvent(result);

      } else {
        throw new Error('Failed to add item to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.#showError();
    } finally {
      this.#setLoadingState(false);
    }
  }

  /**
   * Sets loading state
   * @param {boolean} loading 
   */
  #setLoadingState(loading) {
    const button = this.querySelector('[data-add-button]');
    const spinner = this.querySelector('[data-spinner]');

    if (!(button instanceof HTMLButtonElement)) return;

    if (loading) {
      // Remove outros estados primeiro
      button.classList.remove('cart-order-bump__add-button--success', 'cart-order-bump__add-button--error');
      // Adiciona estado de loading
      button.classList.add('cart-order-bump__add-button--loading');
      button.disabled = true;
      
      // Força o spinner a aparecer como fallback
      if (spinner instanceof HTMLElement) {
        spinner.style.display = 'block';
        spinner.style.position = 'absolute';
        spinner.style.top = '50%';
        spinner.style.left = '50%';
        spinner.style.transform = 'translate(-50%, -50%)';
      }
    } else {
      // Remove estado de loading
      button.classList.remove('cart-order-bump__add-button--loading');
      button.disabled = false;
      
      // Esconde spinner
      if (spinner instanceof HTMLElement) {
        spinner.style.display = 'none';
      }
    }
  }

  /**
   * Sets success state briefly
   */
  #setSuccessState() {
    const button = this.querySelector('[data-add-button]');
    const buttonText = this.querySelector('[data-add-text]');

    if (!(button instanceof HTMLButtonElement) || !buttonText) return;

    // Show success state
    button.classList.add('cart-order-bump__add-button--success');
    button.disabled = true; // Disable button during success state
    const originalText = buttonText.textContent;
    buttonText.textContent = 'Adicionado!';

    // Reset after 1.5 seconds
    setTimeout(() => {
      button.classList.remove('cart-order-bump__add-button--success');
      button.disabled = false; // Re-enable button
      buttonText.textContent = originalText;
    }, 1500);
  }

  /**
   * Dispatches custom order bump event for analytics
   * @param {any} cartData 
   */
  #dispatchOrderBumpEvent(cartData) {
    const variant = this.variantsData && this.variantsData.find((/** @type {any} */ v) => v.id == this.currentVariantId);
    
    // Custom event for analytics/datalayer
    const orderBumpEvent = new CustomEvent('orderBumpAdd', {
      bubbles: true,
      detail: {
        product: this.productData,
        variant: variant,
        cartData: cartData,
        source: 'cart-order-bump'
      }
    });
    
    document.dispatchEvent(orderBumpEvent);

    // DataLayer event for Google Analytics
    if (typeof window !== 'undefined' && /** @type {any} */ (window).dataLayer && variant) {
      /** @type {any} */ (window).dataLayer.push({
        event: 'order_bump_add_to_cart',
        ecommerce: {
          currency: 'BRL',
          value: (variant.price || 0) / 100,
          items: [{
            item_id: variant.sku || variant.id || '',
            item_name: this.productData?.title || '',
            item_variant: variant.title || '',
            price: (variant.price || 0) / 100,
            quantity: 1
          }]
        }
      });
    }
  }

  /**
   * Dispatches standard cart update event
   * @param {any} cartData 
   */
  #dispatchCartUpdateEvent(cartData) {
    // Use the same CartAddEvent that pdp-bundle uses to properly update cart drawer
    document.dispatchEvent(new CartAddEvent(
      cartData,
      this.currentVariantId || '',
      {
        source: 'cart-order-bump',
        itemCount: cartData && cartData.item_count ? cartData.item_count : 1,
        productId: this.productData && this.productData.id ? this.productData.id : ''
      }
    ));
  }



  /**
   * Shows error state
   */
  #showError() {
    const button = this.querySelector('[data-add-button]');
    const buttonText = this.querySelector('[data-add-text]');
    
    if (!(button instanceof HTMLButtonElement) || !buttonText) return;

    // Show error state
    button.classList.add('cart-order-bump__add-button--error');
    button.disabled = true; // Disable button during error state
    const originalText = buttonText.textContent;
    buttonText.textContent = 'Erro. Tente novamente';
    
    // Reset after 3 seconds
    setTimeout(() => {
      button.classList.remove('cart-order-bump__add-button--error');
      button.disabled = false; // Re-enable button
      buttonText.textContent = originalText;
    }, 3000);
  }

  /**
   * Formats money using Shopify's money format
   * @param {number} cents 
   * @returns {string}
   */
  #formatMoney(cents) {
    const amount = cents / 100;
    return amount.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  }
}

customElements.define('cart-order-bump-product', CartOrderBumpProduct); 